import React from "react";
import { HighlightText } from "../../components/HighlightText";
import { interpolate, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { SvgIcon } from "../../components/SvgComponents";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";


interface SvgHeroInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

export const SvgHeroInsights: React.FC<SvgHeroInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs,
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Smooth slide-fade transitions
  const contentOpacity = interpolate(elapsedFrames, [0, 8], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const contentTranslateY = interpolate(elapsedFrames, [0, 10], [25, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const svgScale = interpolate(elapsedFrames, [0, 12], [0.8, 1.0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const floatY = Math.sin(elapsedFrames * 0.05) * 8;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        flex: 1,
        padding: "0 0 240px 0", // Leave space at the bottom for footer watermark
        boxSizing: "border-box",
        position: "relative",
        zIndex: 10,
      }}
    >
      {/* Middle: SVG Icon Container */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          flex: 1,
          maxHeight: "50%",
          opacity: contentOpacity,
          transform: `scale(${svgScale}) translateY(${floatY}px)`,
          filter: "drop-shadow(0 20px 40px rgba(0, 242, 254, 0.2))",
          zIndex: 2,
        }}
      >
        {(() => {
          const brand = detectBrandInText(activeBullet.text);
          return brand ? (
            <CompanyBadge
              name={brand}
              color="#ffffff"
              accentColor="#ffc72c"
              size={220}
              style="badge"
            />
          ) : (
            <SvgIcon
              name={activeBullet.icon || "undraw-astronaut-0o7w"}
              width={300}
              height={300}
              primaryColor="#00f2fe"
            />
          );
        })()}
      </div>

      {/* Bottom: Yellow Card Container */}
      <div
        style={{
          width: "100%",
          padding: "0 45px",
          boxSizing: "border-box",
          opacity: contentOpacity,
          transform: `translateY(${contentTranslateY}px)`,
          zIndex: 3,
        }}
      >
        <div
          style={{
            width: "100%",
            background: "#ffc72c", // Vibrant Warm Yellow
            borderRadius: "40px 40px 0 40px", // Asymmetrical Rounded corners
            boxShadow: "0 25px 60px rgba(0, 0, 0, 0.3)",
            padding: "40px",
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            position: "relative",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {/* Active bullet indicator in header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-body)",
                fontWeight: 800,
                fontSize: "14px",
                color: "rgba(12, 13, 18, 0.6)",
                letterSpacing: "2.5px",
                textTransform: "uppercase",
              }}
            >
              Today's Insight — {activeIndex + 1}
            </div>
            
            {/* Custom styled step pill indicators */}
            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
              {bullets.map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: i === activeIndex ? 24 : 8,
                    height: 8,
                    borderRadius: 4,
                    background: i === activeIndex ? "#0c0d12" : "rgba(12, 13, 18, 0.2)",
                  }}
                />
              ))}
            </div>
          </div>

          {/* Bullet Text */}
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "44px",
              fontWeight: 800,
              lineHeight: 1.35,
              color: "#0c0d12",
              textAlign: "left",
              letterSpacing: "-1px",
              margin: "10px 0 0 0",
            }}
          >
            <HighlightText
              text={activeBullet.text}
              highlights={activeBullet.highlights}
              highlightStyle={{
                fontWeight: 900,
                color: "#0c0d12",
                background: "rgba(0, 168, 120, 0.15)", // Emerald green highlight block
                padding: "2px 6px",
                borderRadius: "8px",
                display: "inline",
              }}
            />
          </p>
        </div>
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
